import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Button,
  Menu,
  MenuItem,
} from '@mui/material';
import * as Icons from '../../constants/icons';
import { useMasterData } from '../../providers/MasterDataProvider';
import { MONTH_ORDER } from '../profitAndLoss/constants';
import { computeDeptSeries, computeBranchSeries, isMonthLocked } from './helpers';
import { type RecruitmentPlanRow, type PlanEdits } from '../../hooks/useRecruitmentPlan';

// ヘッダー行の高さ(px)。直下に全社合計行、さらに下に拠点バナー行を sticky 固定する。
const HEADER_H = 37;
// 全社合計行の高さ(px)。拠点バナーはヘッダー + 全社合計行の分だけ下げて固定する。
// 全社合計行と拠点バナーの間に出る 1px の隙間を詰めるため、実高さより 1px 小さくして重ねる。
const GRAND_H = 33;
// 拠点バナー行の高さ(px)。部門サブバナーを「ヘッダー + 全社合計 + 拠点バナー」の分だけ
// 下げて sticky 固定する際の上積みオフセット (隙間が出る場合は ±1px で微調整)。
const BRANCH_H = 36;
// 部門サブバナーの sticky 固定位置 (拠点バナーの直下)
const DEPT_STICKY_TOP = HEADER_H + GRAND_H + BRANCH_H;

// 役職区分の表示順 (スプレッドシートの階層に倣う)。未定義は末尾、同順は名称順。
// 「一般」の下に雇用形態ベースの増員計画カテゴリ (契約/パート/派遣/出向) を並べる。
const POSITION_ORDER = [
  '代表取締役社長', '常務取締役', '執行役員', '部長', '次長', '課長代理',
  '支店長', 'エリアマネージャー', 'スーパーバイザー', 'プロダクトマネージャー',
  'チーフ', 'リーダー', '係長', '係長補佐', '上席主任', '主任', '一般',
  '契約', 'パート', '派遣', '出向',
  'ＦＲ(フィールドリサーチャー)', 'FR', '未設定',
];
// 役職データには無いが、増員計画のカテゴリとして「＋役職を追加」で選べるようにする雇用形態区分。
// 実績在籍は役職側に計上されるため、これらは実績0の計画専用カテゴリとして扱う。
const PLANNING_POSITIONS = ['契約', 'パート', '派遣', '出向'];

type Props = {
  planRows: RecruitmentPlanRow[];
  edits: PlanEdits;
  setEdit: (branchCode: string, deptCode: string, position: string, month: number, value: number) => void;
  addDept: (branchCode: string, branchName: string, deptCode: string, deptName: string) => void;
  addPosition: (branchCode: string, branchName: string, deptCode: string, deptName: string, position: string) => void;
  addBranch: (branchCode: string, branchName: string) => void;
  addedBranches: { code: string; name: string }[];
  // 役職マスタ (mst_position) の表示順リスト。役職追加の候補・並び順の正本
  availablePositions: { position_name: string; sort_no: number | null }[];
  fiscalYear: number;
};

/**
 * 編集セル。ローカル state で打鍵を受け、onBlur で親へ確定する。
 * これにより、1セル入力中にグリッド全体が打鍵ごとに再描画されるのを防ぐ。
 */
const EditableCell = React.memo(function EditableCell({
  value,
  color,
  onCommit,
}: {
  value: number;
  color: string;
  onCommit: (v: number) => void;
}) {
  const [local, setLocal] = useState<string>(String(value));
  useEffect(() => {
    setLocal(String(value));
  }, [value]);
  return (
    <TextField
      value={local}
      variant="standard"
      type="number"
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => {
        const n = Math.max(0, parseInt(local, 10) || 0);
        setLocal(String(n));
        onCommit(n);
      }}
      onFocus={(e) => e.target.select()}
      inputProps={{
        min: 0,
        'aria-label': '計画人数',
        style: { textAlign: 'right', fontSize: '13px', padding: '1px 0', color, fontWeight: 600 },
      }}
      sx={{
        width: '52px',
        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
          WebkitAppearance: 'none',
          margin: 0,
        },
        '& input[type=number]': { MozAppearance: 'textfield' },
      }}
    />
  );
});

type DeptGroup = { code: string; name: string; rows: RecruitmentPlanRow[] };
type BranchGroup = { code: string; name: string; depts: DeptGroup[]; rows: RecruitmentPlanRow[] };

/**
 * 人事採用 計画 編集グリッド (支店×部門×役職 ツリービュー・増員計画)。
 * 初期表示は支店行 (各月の採用予定合計・読取) のみ。支店を開くと配下部門、部門を開くと
 * 役職区分が現れ、役職ごとに 実績在籍 / 採用予定(+) / 計画在籍 の3行を編集できる。
 * 各部門末尾の「＋役職を追加」、各支店末尾の「＋部署を追加」、ツリー末尾の「＋拠点を追加」から、
 * まだ無い役職・部署・拠点 (mst_branch にあるが従業員0の新拠点を含む) を計画行として足せる。
 *
 * スクロール時、拠点バナー行はヘッダー直下に position: sticky で固定する。
 * これを「拠点グループ = 1 tbody」構成にすることで、次の拠点が来ると前の拠点見出しが
 * 押し出される (iOS 風のグループ見出し) 挙動になる。
 */
export default function RecruitmentPlanGrid({ planRows, edits, setEdit, addDept, addPosition, addBranch, addedBranches, availablePositions, fiscalYear }: Props) {
  const { departmentList, branchList } = useMasterData();

  const now = new Date();
  const lockedByMonth: Record<number, boolean> = {};
  MONTH_ORDER.forEach((m) => {
    lockedByMonth[m] = isMonthLocked(fiscalYear, m, now);
  });

  // 役職の並び順。mst_position (availablePositions) の表示順を最優先し、マスタに無い名称
  // (雇用形態区分の契約/パート/派遣/出向・未設定・旧FR表記など) は POSITION_ORDER でフォールバックする。
  // マスタ未取得時 (初期描画) は POSITION_ORDER のみで従来どおり並ぶ。
  const positionRank = useMemo(() => {
    const masterOrder = new Map(availablePositions.map((p, i) => [p.position_name, i]));
    return (p: string): number => {
      const mi = masterOrder.get(p);
      if (mi !== undefined) return mi; // マスタ掲載の役職を先頭側へ
      const i = POSITION_ORDER.indexOf(p);
      return 1000 + (i === -1 ? 500 : i); // マスタ外はマスタ役職の後ろへ
    };
  }, [availablePositions]);

  // 支店 → 部門 → 役職 でグループ化 (バックエンドは支店→部門→役職順でソート済み)
  const branches: BranchGroup[] = [];
  const branchIndex = new Map<string, number>();
  planRows.forEach((r) => {
    if (!branchIndex.has(r.branch)) {
      branchIndex.set(r.branch, branches.length);
      branches.push({ code: r.branch, name: r.branch_name || '(拠点未設定)', depts: [], rows: [] });
    }
    const branch = branches[branchIndex.get(r.branch)!];
    branch.rows.push(r);
    let dept = branch.depts.find((d) => d.code === r.department);
    if (!dept) {
      dept = { code: r.department, name: r.department_name || '(部署未設定)', rows: [] };
      branch.depts.push(dept);
    }
    dept.rows.push(r);
  });
  // 部門内の役職を表示順に並べ替え
  branches.forEach((b) =>
    b.depts.forEach((d) =>
      d.rows.sort((a, b2) => positionRank(a.position) - positionRank(b2.position) || a.position.localeCompare(b2.position)),
    ),
  );

  // 役職追加メニューの候補母集合 = 役職マスタ(mst_position) + 実在役職 + 増員計画用の雇用形態区分 (契約/パート/派遣/出向)。
  // マスタを含めることで、まだ在籍者がいない役職 (例: 課長) も追加候補に出せる。
  const allPositions = Array.from(
    new Set([...availablePositions.map((p) => p.position_name), ...planRows.map((r) => r.position), ...PLANNING_POSITIONS]),
  ).sort((a, b) => positionRank(a) - positionRank(b) || a.localeCompare(b));

  // ユーザー追加の空拠点 (まだ planRows に行が無いもの) を空グループとして末尾に出す。
  // 部署を足すと addedRows 経由で planRows に現れ、自動的に上の branches 側へ移る。
  const addedBranchGroups: BranchGroup[] = addedBranches
    .filter((b) => !branchIndex.has(b.code))
    .map((b) => ({ code: b.code, name: b.name, depts: [], rows: [] }));
  const allBranchGroups = [...branches, ...addedBranchGroups];

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggle = (key: string) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  const deptKey = (branchCode: string, deptCode: string) => `${branchCode}__${deptCode}`;

  // 追加メニュー (拠点 / 部署 / 役職)
  type MenuState =
    | { type: 'branch'; anchorEl: HTMLElement; candidates: any[] }
    | { type: 'dept'; anchorEl: HTMLElement; branch: BranchGroup; candidates: any[] }
    | { type: 'position'; anchorEl: HTMLElement; branch: BranchGroup; dept: DeptGroup; candidates: string[] }
    | null;
  const [menu, setMenu] = useState<MenuState>(null);

  // 追加できる拠点候補 (mst_branch のうち、現在ツリーに無い拠点)
  const branchCandidates = (): any[] => {
    const present = new Set<string>([...branches.map((b) => b.code), ...addedBranchGroups.map((b) => b.code)]);
    return (branchList || [])
      .filter((b: any) => b && b.branch_code)
      .filter((b: any) => !present.has(b.branch_code))
      .sort((a: any, b: any) => String(a.branch_code).localeCompare(String(b.branch_code)));
  };

  // その拠点に追加できる部署候補 (既存と重複しない・9000共通を除く・5000番台は本社拠点のみ)
  const deptCandidatesFor = (branch: BranchGroup): any[] => {
    const present = new Set(branch.depts.map((d) => d.code));
    const isHonsha = (branch.name || '').includes('本社');
    return (departmentList || [])
      .filter((d: any) => d && d.department_code)
      .filter((d: any) => !present.has(d.department_code))
      .filter((d: any) => d.department_code !== '9000')
      .filter((d: any) => isHonsha || !String(d.department_code).startsWith('5'))
      .sort((a: any, b: any) => String(a.department_code).localeCompare(String(b.department_code)));
  };

  // その部門に追加できる役職候補 (全社で実在する役職のうち、当該部門に未配置のもの)
  const positionCandidatesFor = (dept: DeptGroup): string[] => {
    const present = new Set(dept.rows.map((r) => r.position));
    return allPositions.filter((p) => !present.has(p));
  };

  // 左端見出しセルの共通 sx (横スクロール固定)。縦 sticky 行 (全社合計/拠点/部門バナー) より
  // 低い z にして、それらの下へ潜らせる。
  const stickyLabel = (bg: string, pl: number): object => ({
    position: 'sticky',
    left: 0,
    zIndex: 7,
    backgroundColor: bg,
    pl,
    whiteSpace: 'nowrap',
    borderBottom: '1px dashed #f1f5f9',
  });

  // 純増減セル (拠点バナー・部門サブバナー・役職サブバナーで共有)。
  // sticky を渡すと縦固定する (部門サブバナーで使用)。
  const netCells = (
    series: { m: number; net: number }[],
    bg: string,
    lockedBg: string,
    borderBottom: string,
    sticky?: { top: number; zIndex: number },
  ) =>
    series.map((s) => (
      <TableCell
        key={s.m}
        align="right"
        sx={{
          fontSize: '13px',
          fontWeight: 700,
          color: s.net > 0 ? '#059669' : s.net < 0 ? '#ef4444' : '#94a3b8',
          backgroundColor: lockedByMonth[s.m] ? lockedBg : bg,
          borderBottom,
          ...(sticky ? { position: 'sticky', top: sticky.top, zIndex: sticky.zIndex } : {}),
        }}
      >
        {s.net > 0 ? `+${s.net}` : s.net}
      </TableCell>
    ));

  return (
    <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: '0 1px 3px rgb(0 0 0 / 0.12)', overflow: 'auto', flex: 1, minHeight: 0, maxHeight: '100%' }}>
      <Table stickyHeader size="small" sx={{ minWidth: 1000 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ backgroundColor: '#f5f5f5', color: 'grey.600', fontWeight: 600, fontSize: '0.8125rem', whiteSpace: 'nowrap', minWidth: '240px', position: 'sticky', left: 0, top: 0, zIndex: 15 }}>
              拠点 / 部門 / 役職 / 指標
            </TableCell>
            {MONTH_ORDER.map((m) => (
              <TableCell
                key={m}
                align="right"
                sx={{
                  backgroundColor: lockedByMonth[m] ? '#f1f5f9' : '#f5f5f5',
                  color: lockedByMonth[m] ? '#94a3b8' : 'grey.600',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  whiteSpace: 'nowrap',
                  minWidth: '64px',
                  top: 0,
                  zIndex: 14,
                }}
              >
                {m}月
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        {/* 全社 計画在籍 合計 (全拠点×全部門×全役職の合算)。ヘッダー直下に常時 sticky 固定 */}
        {(() => {
          const grand = computeBranchSeries(planRows, edits);
          return (
            <TableBody>
              <TableRow>
                <TableCell sx={{ position: 'sticky', left: 0, top: HEADER_H, zIndex: 13, backgroundColor: '#1e293b', color: '#ffffff', pl: 1.5, whiteSpace: 'nowrap', fontWeight: 800, fontSize: '13px', borderBottom: '2px solid #334155' }}>
                  全社 計画在籍 合計
                </TableCell>
                {grand.map((s) => (
                  <TableCell key={s.m} align="right" sx={{ position: 'sticky', top: HEADER_H, zIndex: 12, backgroundColor: '#1e293b', color: '#ffffff', fontWeight: 800, fontSize: '13px', borderBottom: '2px solid #334155' }}>
                    {s.planned.toLocaleString()}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          );
        })()}

        {/* 拠点グループ = 1 tbody。バナー行をヘッダー直下に sticky 固定し、次拠点で押し出す */}
        {allBranchGroups.map((branch) => {
          const isBranchOpen = !!expanded[branch.code];
          const branchSeries = computeBranchSeries(branch.rows, edits);
          return (
            <TableBody key={branch.code}>
              {/* 支店バナー行 (sticky・クリックで開閉)。月セルは純増減合計 (読取) */}
              <TableRow
                onClick={() => toggle(branch.code)}
                sx={{ cursor: 'pointer', '&:hover .recruitBranchCell': { backgroundColor: '#e0edff' } }}
              >
                <TableCell
                  className="recruitBranchCell"
                  sx={{ position: 'sticky', left: 0, top: HEADER_H + GRAND_H, zIndex: 11, backgroundColor: '#eff6ff', pl: 1, whiteSpace: 'nowrap', fontWeight: 800, color: '#1e3a8a', borderBottom: '2px solid #bfdbfe' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton size="small" sx={{ mr: 0.5, p: 0.25, color: '#3b82f6' }}>
                      {isBranchOpen ? <Icons.ExpandMore fontSize="small" /> : <Icons.KeyboardArrowRight fontSize="small" />}
                    </IconButton>
                    {branch.name}
                    <Box component="span" sx={{ ml: 1, fontSize: '11px', fontWeight: 600, color: '#64748b' }}>採用予定</Box>
                  </Box>
                </TableCell>
                {branchSeries.map((s) => (
                  <TableCell
                    key={s.m}
                    align="right"
                    className="recruitBranchCell"
                    sx={{
                      position: 'sticky',
                      top: HEADER_H + GRAND_H,
                      zIndex: 10,
                      fontSize: '13px',
                      fontWeight: 700,
                      color: s.net > 0 ? '#059669' : s.net < 0 ? '#ef4444' : '#94a3b8',
                      backgroundColor: lockedByMonth[s.m] ? '#e2e8f0' : '#eff6ff',
                      borderBottom: '2px solid #bfdbfe',
                    }}
                  >
                    {s.net > 0 ? `+${s.net}` : s.net}
                  </TableCell>
                ))}
              </TableRow>

              {/* 展開時 配下部門 */}
              {isBranchOpen &&
                branch.depts.map((dept) => {
                  const dKey = deptKey(branch.code, dept.code);
                  const isDeptOpen = !!expanded[dKey];
                  const deptSeries = computeBranchSeries(dept.rows, edits);
                  return (
                    <React.Fragment key={dKey}>
                      {/* 部門サブバナー (クリックで開閉・純増減小計)。拠点バナー直下に sticky 固定 */}
                      <TableRow
                        onClick={() => toggle(dKey)}
                        sx={{ cursor: 'pointer', backgroundColor: '#f1f5f9', '&:hover': { backgroundColor: '#e8eef6' } }}
                      >
                        <TableCell sx={{ ...stickyLabel('#f1f5f9', 3), top: DEPT_STICKY_TOP, zIndex: 9, fontWeight: 700, color: '#334155', borderBottom: '1px solid #e2e8f0' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton size="small" sx={{ mr: 0.5, p: 0.25, color: '#94a3b8' }}>
                              {isDeptOpen ? <Icons.ExpandMore fontSize="small" /> : <Icons.KeyboardArrowRight fontSize="small" />}
                            </IconButton>
                            {dept.name}
                            <Box component="span" sx={{ ml: 1, fontSize: '11px', fontWeight: 600, color: '#94a3b8' }}>採用予定</Box>
                          </Box>
                        </TableCell>
                        {netCells(deptSeries, '#f1f5f9', '#e2e8f0', '1px solid #e2e8f0', { top: DEPT_STICKY_TOP, zIndex: 8 })}
                      </TableRow>

                      {/* 展開時 配下役職 (各4行) */}
                      {isDeptOpen &&
                        dept.rows.map((row) => {
                          const series = computeDeptSeries(row, edits);
                          return (
                            <React.Fragment key={`${dKey}-${row.position}`}>
                              {/* 役職サブバナー (純増減) */}
                              <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                                <TableCell sx={{ ...stickyLabel('#f8fafc', 5), fontWeight: 700, color: '#475569', fontSize: '13px', borderBottom: '1px solid #eef2f7' }}>
                                  {row.position}
                                </TableCell>
                                {netCells(series, '#f8fafc', '#eef2f7', '1px solid #eef2f7')}
                              </TableRow>

                              {/* 実績在籍 (参照) */}
                              <TableRow>
                                <TableCell sx={{ ...stickyLabel('#ffffff', 7.5), fontSize: '13px', color: '#64748b' }}>実績在籍</TableCell>
                                {series.map((s) => (
                                  <TableCell key={s.m} align="right" sx={{ fontSize: '13px', color: '#94a3b8', backgroundColor: lockedByMonth[s.m] ? '#f8fafc' : 'transparent', borderBottom: '1px dashed #f1f5f9' }}>
                                    {s.headcount.toLocaleString()}
                                  </TableCell>
                                ))}
                              </TableRow>

                              {/* 採用予定 (+) */}
                              <TableRow>
                                <TableCell sx={{ ...stickyLabel('#ffffff', 7.5), fontSize: '13px', color: '#059669' }}>採用予定 (+)</TableCell>
                                {series.map((s) => (
                                  <TableCell key={s.m} align="right" sx={{ backgroundColor: lockedByMonth[s.m] ? '#f8fafc' : 'transparent', borderBottom: '1px dashed #f1f5f9', py: 0.25 }}>
                                    {lockedByMonth[s.m] ? (
                                      <Box component="span" sx={{ fontSize: '13px', color: '#cbd5e1', fontWeight: 600 }}>{s.hires > 0 ? `+${s.hires}` : '0'}</Box>
                                    ) : (
                                      <EditableCell value={s.hires} color="#059669" onCommit={(v) => setEdit(row.branch, row.department, row.position, s.m, v)} />
                                    )}
                                  </TableCell>
                                ))}
                              </TableRow>

                              {/* 計画在籍 (算出) */}
                              <TableRow>
                                <TableCell sx={{ ...stickyLabel('#ffffff', 7.5), fontSize: '13px', color: '#1e293b', fontWeight: 700 }}>計画在籍</TableCell>
                                {series.map((s) => (
                                  <TableCell key={s.m} align="right" sx={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', backgroundColor: lockedByMonth[s.m] ? '#f8fafc' : '#f0f9ff', borderBottom: '1px dashed #e2e8f0' }}>
                                    {s.planned.toLocaleString()}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </React.Fragment>
                          );
                        })}

                      {/* ＋役職を追加 */}
                      {isDeptOpen && (
                        <TableRow>
                          <TableCell sx={{ ...stickyLabel('#ffffff', 6), borderBottom: '1px solid #eef2f7' }}>
                            <Button
                              size="small"
                              startIcon={<Icons.Add sx={{ fontSize: 16 }} />}
                              onClick={(e) => setMenu({ type: 'position', anchorEl: e.currentTarget, branch, dept, candidates: positionCandidatesFor(dept) })}
                              sx={{ textTransform: 'none', fontSize: '12px', color: '#3b82f6' }}
                            >
                              役職を追加
                            </Button>
                          </TableCell>
                          {MONTH_ORDER.map((m) => (
                            <TableCell key={m} sx={{ borderBottom: '1px solid #eef2f7' }} />
                          ))}
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}

              {/* ＋部署を追加 */}
              {isBranchOpen && (
                <TableRow>
                  <TableCell sx={{ ...stickyLabel('#ffffff', 3), borderBottom: '1px solid #e2e8f0' }}>
                    <Button
                      size="small"
                      startIcon={<Icons.Add sx={{ fontSize: 16 }} />}
                      onClick={(e) => setMenu({ type: 'dept', anchorEl: e.currentTarget, branch, candidates: deptCandidatesFor(branch) })}
                      sx={{ textTransform: 'none', fontSize: '12px', color: '#3b82f6' }}
                    >
                      部署を追加
                    </Button>
                  </TableCell>
                  {MONTH_ORDER.map((m) => (
                    <TableCell key={m} sx={{ borderBottom: '1px solid #e2e8f0' }} />
                  ))}
                </TableRow>
              )}
            </TableBody>
          );
        })}

        {/* ＋拠点を追加 (mst_branch の未表示拠点を計画ツリーへ。従業員0の新拠点向け) */}
        <TableBody>
          <TableRow>
            <TableCell sx={{ ...stickyLabel('#ffffff', 1), borderBottom: 'none', py: 1 }}>
              <Button
                size="small"
                startIcon={<Icons.Add sx={{ fontSize: 16 }} />}
                onClick={(e) => setMenu({ type: 'branch', anchorEl: e.currentTarget, candidates: branchCandidates() })}
                sx={{ textTransform: 'none', fontSize: '12px', fontWeight: 700, color: '#2563eb' }}
              >
                拠点を追加
              </Button>
            </TableCell>
            {MONTH_ORDER.map((m) => (
              <TableCell key={m} sx={{ borderBottom: 'none' }} />
            ))}
          </TableRow>
        </TableBody>
      </Table>

      {/* 拠点 / 部署 / 役職 追加の候補メニュー */}
      <Menu anchorEl={menu?.anchorEl ?? null} open={!!menu} onClose={() => setMenu(null)}>
        {menu?.type === 'branch' && menu.candidates.length === 0 && (
          <MenuItem disabled sx={{ fontSize: '13px' }}>追加できる拠点はありません</MenuItem>
        )}
        {menu?.type === 'branch' &&
          menu.candidates.map((b: any) => (
            <MenuItem
              key={b.branch_code}
              sx={{ fontSize: '13px' }}
              onClick={() => {
                addBranch(b.branch_code, b.branch_name || '');
                setExpanded((prev) => ({ ...prev, [b.branch_code]: true }));
                setMenu(null);
              }}
            >
              {(b.branch_name || '(名称なし)') + '（' + b.branch_code + '）'}
            </MenuItem>
          ))}

        {menu?.type === 'dept' && menu.candidates.length === 0 && (
          <MenuItem disabled sx={{ fontSize: '13px' }}>追加できる部署はありません</MenuItem>
        )}
        {menu?.type === 'dept' &&
          menu.candidates.map((d: any) => (
            <MenuItem
              key={d.department_code}
              sx={{ fontSize: '13px' }}
              onClick={() => {
                addDept(menu.branch.code, menu.branch.name, d.department_code, d.department_name || '');
                setExpanded((prev) => ({ ...prev, [menu.branch.code]: true, [deptKey(menu.branch.code, d.department_code)]: true }));
                setMenu(null);
              }}
            >
              {(d.department_name || '(名称なし)') + '（' + d.department_code + '）'}
            </MenuItem>
          ))}

        {menu?.type === 'position' && menu.candidates.length === 0 && (
          <MenuItem disabled sx={{ fontSize: '13px' }}>追加できる役職はありません</MenuItem>
        )}
        {menu?.type === 'position' &&
          menu.candidates.map((p) => (
            <MenuItem
              key={p}
              sx={{ fontSize: '13px' }}
              onClick={() => {
                addPosition(menu.branch.code, menu.branch.name, menu.dept.code, menu.dept.name, p);
                setExpanded((prev) => ({ ...prev, [deptKey(menu.branch.code, menu.dept.code)]: true }));
                setMenu(null);
              }}
            >
              {p}
            </MenuItem>
          ))}
      </Menu>
    </TableContainer>
  );
}
